"use client";

import type { ReactNode } from "react";
import {
  Controller,
  useFormContext,
  type ControllerProps,
} from "react-hook-form";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import { FormField } from "./FormField";
import { containedCardSx } from "./containedCardSx";

export type RadioOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  /**
   * Optional content rendered inside the card when this option is the
   * selected value. Only honored when the parent group has `contained`.
   */
  content?: ReactNode;
};

export type FormRadioGroupProps = {
  name: string;
  label?: ReactNode;
  helperText?: ReactNode;
  required?: boolean;
  options: RadioOption[];
  row?: boolean;
  rules?: ControllerProps["rules"];
  /**
   * Render each option as a bordered card. The card's border turns
   * brand-blue when that option is selected, and the radio dot is pushed
   * to the right edge of the card.
   */
  contained?: boolean;
};

export const FormRadioGroup = ({
  name,
  label,
  helperText,
  required,
  options,
  row,
  rules,
  contained,
}: FormRadioGroupProps) => {
  const { control } = useFormContext();
  const labelId = `${name}-label`;
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { ref, ...field }, fieldState }) => {
        const hasError = !!fieldState.error;
        return (
          <FormField
            label={label}
            helperText={fieldState.error?.message ?? helperText}
            error={hasError}
            required={required}
            labelId={labelId}
          >
            <RadioGroup
              {...field}
              value={field.value ?? ""}
              row={contained ? false : row}
              aria-labelledby={labelId}
              sx={contained ? { gap: 1.5 } : undefined}
            >
              {options.map((option, index) => {
                const selected = field.value === option.value;
                const radioControl = (
                  <Radio
                    // RHF focuses the first radio when the group fails
                    // validation, matching native browser behavior.
                    slotProps={index === 0 ? { input: { ref } } : undefined}
                    color={hasError ? "error" : "primary"}
                  />
                );
                if (contained) {
                  return (
                    <Box
                      key={option.value}
                      sx={containedCardSx(selected, hasError)}
                    >
                      <Stack spacing={1.5} sx={{ width: "100%" }}>
                        <FormControlLabel
                          value={option.value}
                          disabled={option.disabled}
                          control={radioControl}
                          label={option.label}
                          labelPlacement="start"
                          sx={{
                            margin: 0,
                            width: "100%",
                            justifyContent: "space-between",
                            gap: 2,
                            alignItems: "center",
                          }}
                        />
                        {selected && option.content && (
                          <Box
                            sx={(theme) => ({ paddingLeft: theme.spacing(8) })}
                          >
                            {option.content}
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  );
                }
                return (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    control={radioControl}
                    label={option.label}
                    // MUI's default `ml: -11px` is designed around Radio's stock
                    // 9px padding — our Radio override sets padding to 0, so we
                    // need to reset the negative pull and add an explicit gap
                    // (spacing/8) between the dot and the label text.
                    sx={{ marginLeft: 0, gap: 2 }}
                  />
                );
              })}
            </RadioGroup>
          </FormField>
        );
      }}
    />
  );
};
